import { FC } from 'react';

import { Invoice } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { useInvoiceDocument } from '../../../../../hooks/use-invoice-document.hook';

type Props = {
   invoice: Invoice;
};

const SHOW_TOOLTIP_LENGTH = 18;

export const ClosedInvoiceTableRow: FC<Props> = ({ invoice }) => {
   const { isDownloading, handleDownload, handleView } = useInvoiceDocument();
   const translate = useTranslations();
   const format = useFormatter();

   const showTooltipOnInvoiceId = invoice.id.length > SHOW_TOOLTIP_LENGTH;

   const invoicePayload = invoice.documentId
      ? { invoiceId: invoice.id, documentId: invoice.documentId, date: invoice.createDate, type: invoice.type, eInvoiceId: invoice.eInvoiceId }
      : null;

   return (
      <TableRow>
         <TableCell>
            <Box direction="column" gap="3xs">
               {showTooltipOnInvoiceId && (
                  <Tooltip text={invoice.eInvoiceId || invoice.documentId.toString()} limitWidth={false}>
                     <span style={{ display: 'block', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        <Typography variant="small">{invoice.eInvoiceId || invoice.documentId}</Typography>
                     </span>
                  </Tooltip>
               )}
               {!showTooltipOnInvoiceId && (
                  <span style={{ display: 'block', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                     <Typography variant="small">{invoice.eInvoiceId || invoice.documentId}</Typography>
                  </span>
               )}
               <Typography component="span" variant="small" color="subtle">
                  {translate(`invoices.invoice-type.${invoice.type}`)}
               </Typography>
            </Box>
         </TableCell>
         <TableCell>
            <Typography variant="small">{format.dateTime(invoice.createDate)}</Typography>
         </TableCell>
         <TableCell>
            <Typography variant="small">{invoice.legalEntity}</Typography>
         </TableCell>
         <TableCell>
            <Typography variant="small">{format.price(invoice.billedSum)}</Typography>
         </TableCell>
         <TableCell>{/* Placeholder */}</TableCell>
         <TableCell align="right">
            {!!invoicePayload && (
               <Box justify="end">
                  {isDownloading ? (
                     <Spinner size="small" />
                  ) : (
                     <Box gap="md">
                        <IconButton
                           icon="download"
                           ariaLabel="Download Invoice"
                           onClick={() => handleDownload(invoicePayload)}
                           showGutters={false}
                           variant="on-light"
                        />
                        <IconButton
                           icon="eye"
                           ariaLabel="View Invoice"
                           onClick={() => handleView(invoicePayload)}
                           showGutters={false}
                           variant="on-light"
                        />
                     </Box>
                  )}
               </Box>
            )}
         </TableCell>
      </TableRow>
   );
};
